/**
 * Time Formatting Utilities
 * Converts between 24-hour (military) and 12-hour (AM/PM) time formats
 */

/**
 * Converts 24-hour time string to 12-hour format with AM/PM
 * @param time24 - Time string in 24-hour format (e.g., "15:00", "09:30")
 * @returns Time string in 12-hour format (e.g., "3:00 PM", "9:30 AM")
 */
export function formatTime12Hour(time24: string): string {
  const [hours24, minutes] = time24.split(':').map(Number);
  const period = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 || 12; // Convert 0 to 12, keep 1-11 as is

  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

/**
 * Converts 12-hour time string to 24-hour format
 * @param time12 - Time string in 12-hour format (e.g., "3:00 PM", "9:30 AM")
 * @returns Time string in 24-hour format (e.g., "15:00", "09:30")
 */
export function formatTime24Hour(time12: string): string {
  const match = time12.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!match) {
    throw new Error('Invalid time format. Expected format: "HH:MM AM/PM"');
  }

  let [, hours, minutes, period] = match;
  let hours24 = parseInt(hours, 10);

  if (period.toUpperCase() === 'PM' && hours24 !== 12) {
    hours24 += 12;
  } else if (period.toUpperCase() === 'AM' && hours24 === 12) {
    hours24 = 0;
  }

  return `${hours24.toString().padStart(2, '0')}:${minutes.padStart(2, '0')}`;
}

/**
 * Formats a Date object to 12-hour time string
 * @param date - Date object
 * @returns Time string in 12-hour format (e.g., "3:00 PM")
 */
export function formatDateTo12Hour(date: Date): string {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;

  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
}
